import React, { useMemo, useState } from "react";
import ReadComplete from "../core/ReadComplete";
import { validateExample } from "./sequenceModel";
import SequenceStep from "./SequenceStep";
import { clearCallItOrder, loadCallItOrder } from "./callItOrder";

// A motion Read (Momentum, Structure), client flow:
//   Teach (one per answer, in read.teachOrder) -> both Call Its in a random
//   order. A missed Call It shows Explain, then the Retry with the same answer,
//   then the remaining Call It. A missed Retry shows the correct answer and
//   Explain and moves on: no repeat loop. The Read completes once both Call It
//   paths and any Retries they triggered are done.
//
// The Call It order is random once per attempt and kept in this browser
// (callItOrder.js). Progress itself is not saved: a reload starts again from
// Teach, with the same order. Nothing is credited to the wallet.
//
//   read   the Read's examples, settings and copy (e.g. momentum/config.js)
export default function SequenceRead({ read }) {
  const { teach, callIts, retryFor, invalid } = useMemo(() => {
    const byStage = (stage) => read.examples.filter((e) => e.stage === stage);
    const retries = (answer) => byStage("retry").find((e) => e.answer === answer);
    const t = read.teachOrder.map((a) => byStage("teach").find((e) => e.answer === a));
    const c = byStage("callIt");
    const list = read.examples.map((e) => [e.id, validateExample(e, read.answers(e))]).filter(([, errs]) => errs.length);
    if (t.some((e) => !e)) list.push(["teach", [`needs one example for each of ${read.teachOrder.join(", ")}`]]);
    if (c.length !== 2) list.push(["callIt", ["needs two examples"]]);
    c.forEach((e) => !retries(e.answer) && list.push([e.id, ["no matching retry example"]]));
    return { teach: t, callIts: c, retryFor: retries, invalid: list };
  }, [read]);

  const [order] = useState(() => loadCallItOrder(callIts.map((e) => e.id), read.orderStorageKey).order);
  const [queue, setQueue] = useState(() => [...teach.map((e) => e?.id), ...order]);
  const [pos, setPos] = useState(0);
  const [missedCallIt, setMissedCallIt] = useState(false);

  if (invalid.length) {
    // eslint-disable-next-line no-console
    console.error(`${read.title}: invalid lesson data`, invalid);
    return <p className="text-sm text-secondary">This Read could not be loaded.</p>;
  }

  if (pos >= queue.length) {
    return (
      <div className="mx-auto max-w-[560px] rounded-2xl border border-gray-200 bg-white p-4 md:p-6">
        <ReadComplete rewards={read.rewards} bonus={!missedCallIt} />
      </div>
    );
  }

  const example = read.examples.find((e) => e.id === queue[pos]);
  const callItNumber = order.indexOf(example.id) + 1;
  const label =
    example.stage === "teach"
      ? `Teach · ${pos + 1} of ${teach.length}`
      : example.stage === "callIt"
      ? `Call it · ${callItNumber} of ${order.length}`
      : "Retry";

  const handleDone = (correct) => {
    const next = [...queue];
    if (example.stage === "callIt" && !correct) {
      next.splice(pos + 1, 0, retryFor(example.answer).id);
      setMissedCallIt(true);
    }
    // TEMPORARY (staged review): the last screen's button does nothing yet.
    if (pos + 1 >= next.length && read.holdCompletion) return;
    if (pos + 1 >= next.length) clearCallItOrder(read.orderStorageKey);
    setQueue(next);
    setPos(pos + 1);
    window.scrollTo({ top: 0 });
  };

  // The button after a missed Call It leads into its Retry.
  const ctaLabel = (correct) => (example.stage === "callIt" && !correct ? "Retry" : "Continue");

  return <SequenceStep key={`${pos}-${example.id}`} read={read} example={example} label={label} ctaLabel={ctaLabel} onDone={handleDone} />;
}
